import { AvailabilitySelect } from 'components/availability-select';
import Button from 'components/button';
import Controls from 'components/controls';
import Loading from 'components/loading';
import Modal from 'components/modal';
import { BookingType, DATE_FMT } from 'config';
import { useAppDispatch } from 'hooks/useAppDispatch';
import {
  AddIcon,
  ErrorFilledIcon,
  SuccessFilledIcon,
  TrashIcon,
  TwoWayArrowIcon,
} from 'icons';
import moment from 'moment-timezone';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getCartAppointmentProduct,
  getCartMultiAvailabilityList,
} from 'state/selectors';
import {
  extendMultiAvailabilityList,
  removeMultiAvailabilityListItem,
} from 'state/slice/cart';

import { Availability, WeeklyAvailability } from 'api/Serializers/Availability';
import { BookingHeader } from '../components';
import { StepProps } from '../types';
import { getDatetimeStr } from '../utils';

const MultiDateTimeStep = ({
  state,
  onClickContinue,
  onClickEdit,
  bookingType,
}: StepProps<undefined>) => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const appointmentProduct = useSelector(getCartAppointmentProduct);
  const availabilityList = useSelector(getCartMultiAvailabilityList);
  const [availabilityToSwap, setAvailabilityToSwap] =
    useState<WeeklyAvailability>();
  const [availabilityToDelete, setAvailabilityToDelete] =
    useState<WeeklyAvailability>();
  const [updateMode, setUpdateMode] = useState<null | 'add' | 'swap'>(null);
  const [confirmContinue, setConfirmContinue] = useState(false);

  const availabilityListIds = (availabilityList ?? []).reduce(
    (accumulator, avail) =>
      avail.id !== undefined ? accumulator.concat(avail.id) : accumulator,
    []
  );

  const handleAvailabilitySelect = (
    selectedAvailability: Availability,
    _: BookingType
  ) => {
    const transformedAvailability: WeeklyAvailability = {
      id: selectedAvailability.id,
      start: selectedAvailability.start,
      end: selectedAvailability.end,
      isBookable: selectedAvailability.isBookable,
    };
    dispatch(extendMultiAvailabilityList([transformedAvailability]));
    if (updateMode === 'swap') {
      dispatch(removeMultiAvailabilityListItem(availabilityToSwap));
      setAvailabilityToSwap(undefined);
    }
    setUpdateMode(null);
  };

  const handleContinueConfirmed = () => {
    availabilityList.forEach((avail) => {
      if (!avail.isBookable) {
        dispatch(removeMultiAvailabilityListItem(avail));
      }
    });
    onClickContinue();
    setConfirmContinue(false);
  };

  const handleDeleteConfirmed = (availability: WeeklyAvailability) => {
    if (availabilityList.length === 1) {
      enqueueSnackbar({
        message: "Can't remove last time",
        variant: 'error',
      });
      return;
    }
    dispatch(removeMultiAvailabilityListItem(availability));
    enqueueSnackbar({
      message: 'Time removed',
      variant: 'success',
    });
    if (availabilityToDelete !== undefined) {
      setAvailabilityToDelete(undefined);
    }
  };

  const checkCanContinue = () => {
    if (availabilityList.some((avail) => !avail.isBookable)) {
      setConfirmContinue(true);
    } else {
      onClickContinue();
    }
  };

  const handleSwapClicked = (availability: WeeklyAvailability) => {
    setAvailabilityToSwap(availability);
    setUpdateMode('swap');
  };

  if (bookingType === 'single' || state === 'hidden') {
    return null;
  } else if (state === 'complete') {
    return (
      <div className="space-y-2">
        <BookingHeader title="Times" onEdit={onClickEdit} />
        <div className="flex flex-col gap-3 justify-center">
          {availabilityList?.map((avail, i) => (
            <div className="w-full" key={`list-avail-${i}`}>
              {getDatetimeStr(appointmentProduct, avail)}
            </div>
          )) ?? '-'}
        </div>
      </div>
    );
  } else if (availabilityList === undefined) {
    return <Loading message="Loading availability..." />;
  } else if (updateMode !== null) {
    return (
      <div className="space-y-4">
        <BookingHeader
          title={
            updateMode === 'swap'
              ? `Replace ${getDatetimeStr(
                  appointmentProduct,
                  availabilityToSwap
                )}`
              : 'Add time'
          }
        />
        <AvailabilitySelect
          appointmentProductId={appointmentProduct.id}
          views={['single']}
          noTimesCTA={null}
          openToDate={
            availabilityToSwap !== undefined
              ? moment(availabilityToSwap.start)
                  .tz(appointmentProduct.timezone)
                  .format(DATE_FMT.DATE_FIELD)
              : undefined
          }
          onTimeSelect={handleAvailabilitySelect}
          availabilityFilter={(avail) =>
            !availabilityListIds.includes(avail.id)
          }
          onBack={() => setUpdateMode(null)}
        />
      </div>
    );
  } else {
    return (
      <>
        <div className="space-y-2">
          <BookingHeader title="Times" />
          <div className="flex flex-col gap-2 justify-center">
            {availabilityList.map((avail, i) => (
              <div
                className="w-full flex justify-between gap-2 py-2"
                key={`list-avail-${i}`}
              >
                <div className="flex items-center gap-2">
                  <div>
                    {avail.isBookable ? (
                      <SuccessFilledIcon
                        width={20}
                        className="text-green-500"
                      />
                    ) : (
                      <ErrorFilledIcon width={20} className="text-red-500" />
                    )}
                  </div>
                  <div>{getDatetimeStr(appointmentProduct, avail)}</div>
                </div>
                <div className="flex justify-center items-center gap-2">
                  {!avail.isBookable && (
                    <div className="text-xs red badge">Unavailable</div>
                  )}
                  <button
                    className="btn-icon text-gray-800 hover:gray hover:text-gray-800"
                    onClick={() => handleDeleteConfirmed(avail)}
                  >
                    <TrashIcon width={22} />
                  </button>
                  <button
                    className="btn-icon text-gray-800 hover:gray hover:text-gray-800"
                    onClick={() => handleSwapClicked(avail)}
                  >
                    <TwoWayArrowIcon width={22} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <Controls>
            <Button
              variant="contained"
              icon={<AddIcon width={18} />}
              onClick={() => setUpdateMode('add')}
            >
              Add time
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={checkCanContinue}
            >
              Continue
            </Button>
          </Controls>
        </div>
        {availabilityToDelete !== undefined && (
          <Modal
            open={availabilityToDelete !== undefined}
            onClose={() => setAvailabilityToDelete(undefined)}
            name="Confirm delete availability"
            title="Remove time?"
          >
            <p>
              Remove {getDatetimeStr(appointmentProduct, availabilityToDelete)}{' '}
              from your pending bookings?
            </p>
            <Controls className="!pb-0">
              <Button
                variant="contained"
                onClick={() => setAvailabilityToDelete(undefined)}
              >
                Back
              </Button>
              <Button
                color="secondary"
                variant="contained"
                onClick={() => handleDeleteConfirmed(availabilityToDelete)}
              >
                Remove
              </Button>
            </Controls>
          </Modal>
        )}
        <Modal
          open={confirmContinue}
          onClose={() => setConfirmContinue(false)}
          name="Confirm multi time continue"
          title="Unavailable times remaining"
        >
          <p>Continue without replacing the following unavailable times?</p>
          <ul className="leading-7 mb-2">
            {availabilityList
              .filter((avail) => !avail.isBookable)
              .map((avail) => (
                <li key={avail.start}>
                  {getDatetimeStr(appointmentProduct, avail)}
                </li>
              ))}
          </ul>
          <Controls className="!pb-0">
            <Button
              variant="contained"
              onClick={() => setConfirmContinue(false)}
            >
              Back
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={handleContinueConfirmed}
            >
              Continue
            </Button>
          </Controls>
        </Modal>
      </>
    );
  }
};

export default MultiDateTimeStep;
